import { atom } from 'jotai';
import { toAnnotationWritePayload } from '../../studio-store/utils/annotation-mappers';
import { annotationFramesAtom } from '../annotation-atoms';
import { eventJournalerAtom } from '../event-journal-atoms';
import { incrementStudioStoreVersionAtom, studioConfigAtom, studioStoreAtom } from '../studio-store';
import { getFramesAfterBulkDelete, getFramesAfterBulkWrite, getFramesAfterDelete, getFramesAfterPromote, getFramesAfterWrite } from './utils/annotation-frames-atom-updates';
import type { StudioWriteAction } from './actions';
import { STUDIO_WRITE_ACTIONS } from './actions';
const VIDEO_ID = 'main';

// Single write entry point for all StudioStore mutations. Each case calls the
// StudioStore method (when initialized) and dual-writes annotationFramesAtom
// (legacy support during migration). storeVersionAtom is incremented after any
// handled action so all derived atoms re-evaluate.
export const dispatchStudioStoreWriteAtom = atom(null, (get, set, action: StudioWriteAction) => {
  const studioStore = get(studioStoreAtom);
  const studioConfig = get(studioConfigAtom);
  const windowedConfig = studioConfig?.mode === 'windowed' ? studioConfig : null;
  const journaler = windowedConfig !== null ? get(eventJournalerAtom) : null;
  switch (action.type) {
    case STUDIO_WRITE_ACTIONS.WRITE_ANNOTATION:
      {
        const {
          frameIndex,
          annotationId,
          annotation
        } = action;
        studioStore?.writeAnnotation?.({
          frameIndex,
          annotationId,
          payload: toAnnotationWritePayload(annotation)
        });
        set(annotationFramesAtom, getFramesAfterWrite(get(annotationFramesAtom), frameIndex, annotationId, annotation));
        if (windowedConfig !== null && journaler !== null) {
          journaler.enqueue('annotation_upsert', windowedConfig.taskKey, VIDEO_ID, frameIndex, annotationId, {
            annotationType: annotation.annotationType,
            data: annotation.data,
            keyFrame: annotation.keyFrame
          });
        }
        break;
      }
    case STUDIO_WRITE_ACTIONS.BULK_WRITE:
      {
        const {
          writes
        } = action;
        studioStore?.bulkWriteAnnotations?.(writes.map(({
          frameIndex,
          annotationId,
          annotation
        }) => ({
          frameIndex,
          annotationId,
          payload: toAnnotationWritePayload(annotation)
        })));
        set(annotationFramesAtom, getFramesAfterBulkWrite(get(annotationFramesAtom), writes));
        if (windowedConfig !== null && journaler !== null) {
          for (const {
            frameIndex,
            annotationId,
            annotation
          } of writes) {
            journaler.enqueue('annotation_upsert', windowedConfig.taskKey, VIDEO_ID, frameIndex, annotationId, {
              annotationType: annotation.annotationType,
              data: annotation.data,
              keyFrame: annotation.keyFrame
            });
          }
        }
        break;
      }
    case STUDIO_WRITE_ACTIONS.DELETE_ANNOTATION:
      {
        const {
          frameIndex,
          annotationId
        } = action;
        studioStore?.deleteAnnotation?.({
          frameIndex,
          annotationId
        });
        set(annotationFramesAtom, getFramesAfterDelete(get(annotationFramesAtom), frameIndex, annotationId));
        if (windowedConfig !== null && journaler !== null) {
          journaler.enqueue('annotation_delete', windowedConfig.taskKey, VIDEO_ID, frameIndex, annotationId);
        }
        break;
      }
    case STUDIO_WRITE_ACTIONS.BULK_DELETE:
      {
        const {
          deletes
        } = action;
        studioStore?.bulkDeleteAnnotations?.(deletes);
        set(annotationFramesAtom, getFramesAfterBulkDelete(get(annotationFramesAtom), deletes));
        if (windowedConfig !== null && journaler !== null) {
          for (const {
            frameIndex,
            annotationId
          } of deletes) {
            journaler.enqueue('annotation_delete', windowedConfig.taskKey, VIDEO_ID, frameIndex, annotationId);
          }
        }
        break;
      }
    case STUDIO_WRITE_ACTIONS.PROMOTE_TO_KEYFRAME:
      {
        const {
          frameIndex,
          annotationId
        } = action;
        const promotedAnnotation = studioStore?.promoteToKeyFrame?.(frameIndex, annotationId) ?? null;
        set(annotationFramesAtom, getFramesAfterPromote(get(annotationFramesAtom), frameIndex, annotationId));
        if (windowedConfig !== null && journaler !== null && promotedAnnotation !== null) {
          journaler.enqueue('annotation_upsert', windowedConfig.taskKey, VIDEO_ID, frameIndex, annotationId, promotedAnnotation);
        }
        break;
      }
    case STUDIO_WRITE_ACTIONS.UPDATE_METADATA:
      {
        const {
          annotationId,
          partialPayload
        } = action;
        studioStore?.updateMetadata?.(annotationId, partialPayload);
        break;
      }
    default:
      // Unhandled action types — do not increment storeVersionAtom.
      return;
  }
  set(incrementStudioStoreVersionAtom);
});